
import React from 'react';
import { AIStaffMember } from '@/lib/types';
import { StaffManagement } from '@/components/StaffManagement';
import StaffHeader from './StaffHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { staffApiClient } from '@/api/staff';
import { useStaffConverter } from '@/hooks/staff/useStaffConverter';
import { useUserBusiness } from '@/hooks/staff/useUserBusiness';

interface StaffDetailViewProps {
  staffId: string;
  aiStaff?: AIStaffMember | null;
}

const StaffDetailView: React.FC<StaffDetailViewProps> = ({ staffId, aiStaff: propAiStaff }) => {
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();
  const { convertStaffData } = useStaffConverter();
  
  // Get current user
  const { data: userId } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 300000, // 5 minutes
  });
  
  // Get business ID
  const { data: businessId } = useQuery({
    queryKey: ['businessId', userId],
    queryFn: () => fetchBusinessId(userId!),
    enabled: !!userId,
    staleTime: 300000, // 5 minutes
  });

  // Use React Query to fetch staff data
  const { data: aiStaff, isLoading } = useQuery({
    queryKey: ['staff', businessId, staffId],
    queryFn: async () => {
      if (!businessId) return null;
      const data = await staffApiClient.fetchStaffById(businessId, staffId);
      return data ? convertStaffData(data) : null;
    },
    enabled: !!businessId && !!staffId && staffId !== ':staffId',
    staleTime: 60000, // 1 minute
    initialData: propAiStaff || undefined
  });

  return (
    <>
      <StaffHeader 
        title={aiStaff ? `Manage ${aiStaff.name}` : 'Staff Management'} 
        backPath="/app/staff"
      />
      
      <Card className="mb-6 border-primary/20">
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center gap-4">
              <Skeleton className="text-4xl w-12 h-12 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-6 w-2/3" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </div>
          ) : aiStaff ? (
            <div className="flex items-center gap-4">
              <div className="text-4xl">{aiStaff.avatar || '🤖'}</div>
              <div>
                <h2 className="text-2xl font-bold">{aiStaff.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="capitalize">{aiStaff.role} Assistant</Badge>
                  <Badge variant="secondary" className="capitalize">{aiStaff.level} Level</Badge>
                </div>
                {aiStaff.hiredAt && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                    <Calendar className="h-3 w-3" />
                    <span>Hired on {new Date(aiStaff.hiredAt).toLocaleDateString()}</span>
                    <Clock className="h-3 w-3 ml-2" />
                    <span>{Math.floor((Date.now() - new Date(aiStaff.hiredAt).getTime()) / (1000 * 60 * 60 * 24))} days</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Info className="h-4 w-4" />
              <span>Staff information couldn't be loaded</span>
            </div>
          )}
        </CardContent>
      </Card>
      
      <StaffManagement specificStaffId={staffId} />
    </>
  );
};

export default StaffDetailView;
